import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, FileText, Clock, Play } from 'lucide-react';

const ValidationPanel = () => {
  const [selectedValidation, setSelectedValidation] = useState(null);

  // Mock validation data
  const validations = [
    {
      id: 1,
      title: 'Document Completeness Check',
      category: 'Documentation',
      status: 'passed',
      lastRun: '2025-12-06 10:30',
      findings: 0,
      completeness: 100
    },
    {
      id: 2,
      title: 'Regulatory Alignment Verification',
      category: 'Compliance',
      status: 'failed',
      lastRun: '2025-12-06 09:15',
      findings: 3,
      completeness: 75
    },
    {
      id: 3,
      title: 'Certification Expiry Check',
      category: 'Certifications',
      status: 'warning',
      lastRun: '2025-12-06 08:00',
      findings: 2,
      completeness: 85
    },
    {
      id: 4,
      title: 'Compliance Gap Analysis',
      category: 'Gap Analysis',
      status: 'passed',
      lastRun: '2025-12-05 16:45',
      findings: 0,
      completeness: 100
    },
    {
      id: 5,
      title: 'Multi-stage Approval Status',
      category: 'Workflow',
      status: 'warning',
      lastRun: '2025-12-06 11:00',
      findings: 1,
      completeness: 90
    }
  ];

  const validationDetails = {
    1: [
      { item: 'Safety Policies', status: 'Complete', details: 'All required documents present' },
      { item: 'Training Records', status: 'Complete', details: 'Up to date for all personnel' },
      { item: 'Audit Reports', status: 'Complete', details: 'All reports filed and reviewed' }
    ],
    2: [
      { item: 'OSHA 1910 Compliance', status: 'Non-compliant', details: 'Missing emergency evacuation procedures' },
      { item: 'EPA Regulations', status: 'Non-compliant', details: 'Air quality monitoring data incomplete' },
      { item: 'NFPA Standards', status: 'Non-compliant', details: 'Fire suppression system inspection overdue' }
    ],
    3: [
      { item: 'Forklift Operator Licenses', status: 'Expiring Soon', details: '2 certifications expire within 30 days' },
      { item: 'First Aid Certifications', status: 'Expiring Soon', details: '1 certification expires within 15 days' }
    ],
    4: [
      { item: 'Safety Equipment', status: 'Compliant', details: 'All equipment within compliance standards' },
      { item: 'Training Programs', status: 'Compliant', details: 'Programs meet regulatory requirements' }
    ],
    5: [
      { item: 'Manager Approval', status: 'Pending', details: 'Awaiting final approval from department head' }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5" />;
      case 'failed':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Validation List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Validation Checks</h2>
          <p className="text-sm text-gray-600 mt-1">
            Automated validation workflows for compliance monitoring
          </p>
        </div>

        <div className="p-4 space-y-3">
          {validations?.map((validation) => (
            <div
              key={validation?.id}
              onClick={() => setSelectedValidation(validation)}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedValidation?.id === validation?.id
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${getStatusColor(validation?.status)}`}>
                      {getStatusIcon(validation?.status)}
                      <span className="text-xs font-medium capitalize">{validation?.status}</span>
                    </div>
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                      {validation?.category}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">{validation?.title}</h3>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>Last run: {validation?.lastRun}</span>
                    <span>{validation?.findings} findings</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {validation?.completeness}%
                  </div>
                  <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        validation?.status === 'passed' ?'bg-green-500'
                          : validation?.status === 'warning' ?'bg-yellow-500' :'bg-red-500'
                      }`}
                      style={{ width: `${validation?.completeness}%` }}
                    />
                  </div>
                </div>
              </div>

              <button className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
                <Play className="w-4 h-4" />
                Run Validation
              </button>
            </div>
          ))}
        </div>
      </div>
      {/* Validation Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Validation Details</h2>
          <p className="text-sm text-gray-600 mt-1">
            {selectedValidation ? selectedValidation?.title : 'Select a validation check to view details'}
          </p>
        </div>

        {selectedValidation ? (
          <div className="p-4">
            <div className="space-y-3">
              {validationDetails?.[selectedValidation?.id]?.map((detail, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{detail?.item}</h4>
                        <span className={`text-xs px-2 py-1 rounded-lg font-medium ${
                          detail?.status?.includes('Complete') || detail?.status?.includes('Compliant')
                            ? 'bg-green-50 text-green-700' : detail?.status?.includes('Pending') || detail?.status?.includes('Expiring')
                            ? 'bg-yellow-50 text-yellow-700' :'bg-red-50 text-red-700'
                        }`}>
                          {detail?.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{detail?.details}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex gap-3">
              <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Generate Report
              </button>
              <button className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                Export Findings
              </button>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Select a validation check to view detailed findings</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationPanel;